// Cart drawer — progressive enhancement over the /cart page.
// Intercepts add-to-cart forms, drives the Cart AJAX API, and re-renders the
// drawer via the Section Rendering API. Zero dependencies. One deferred module.
(() => {
  // Section id: the header-group instance (e.g. sections--19516809052208__cart-drawer),
  // not the literal filename — a hardcoded 'cart-drawer' renders an anonymous
  // default-settings instance via Section Rendering, dropping show_upsells etc.
  const drawer = () => document.getElementById('CartDrawer');
  const sectionId = () => drawer()?.closest('.shopify-section')?.id.replace(/^shopify-section-/, '') || 'cart-drawer';

  // Serial FIFO queue: every action runs in click order and none is dropped. It used
  // to be a one-slot pending action (last-write-wins), which silently discarded
  // everything between the first and the last of a rapid burst. That was invisible
  // while the only rapid actions were repeat clicks on the SAME quantity stepper —
  // the UGC viewer's tagged-product strip is the first surface that invites adding
  // several DIFFERENT products in a row, and there the dropped middle adds are the bug.
  // Last-wins guard for the async upsell fetch: every loadUpsells() bumps this, and
  // a response whose token no longer matches is discarded (a newer refresh — or the
  // mutation that triggered it — has since started). Upsells are a display refresh, so
  // last-wins is right here; cart MUTATIONS queue instead, see run() below.
  let upsellGen = 0;
  // The trailing catch is what keeps the chain alive: a failed action resolves its own
  // link, so the next queued one still runs.
  let chain = Promise.resolve();
  const run = (fn) => {
    chain = chain.then(fn).catch(() => {});
  };

  const open = () => {
    const d = drawer();
    hideToast();
    if (d && !d.open) { d.showModal(); loadUpsells(); }
  };

  // "Stay on the page" after an add (cart drawer setting after_add): a toast in the top
  // layer and a bump on the header count, instead of opening the drawer.
  let toastTimer;
  const toastEl = () => document.querySelector('[data-cart-toast]');
  let toastHome;
  const hideToast = () => {
    const t = toastEl();
    clearTimeout(toastTimer);
    if (t?.matches(':popover-open')) t.hidePopover();
    if (t && toastHome && t.parentElement !== toastHome) toastHome.append(t);
  };
  const toast = (msg) => {
    const t = toastEl();
    if (!t?.showPopover) return false;
    const text = t.querySelector('[data-cart-toast-msg]');
    hideToast();
    toastHome ||= t.parentElement;
    // An open modal (the quick view) makes everything outside it inert: the toast would
    // paint but be unclickable and never announced. Inside the modal it is neither.
    const modal = document.querySelector('dialog:modal');
    if (modal) modal.append(t);
    text.textContent = '';
    t.showPopover(); // re-shown on every add, so it stays above a modal opened since
    // Filled after it is rendered, so the role=status region announces the change.
    setTimeout(() => { text.textContent = msg; }, 60);
    toastTimer = setTimeout(hideToast, 4000);
    return true;
  };
  const bumpCount = () => {
    document.querySelectorAll('[data-header-cart-count]').forEach((el) => {
      el.classList.remove('is-bumped');
      void el.offsetWidth; // restart the animation on a repeat add
      el.classList.add('is-bumped');
    });
  };
  const close = () => {
    const d = drawer();
    if (d && d.open) d.close();
  };

  const setHeaderCount = (count) => {
    document.querySelectorAll('[data-header-cart-count]').forEach((el) => {
      el.textContent = count;
      el.toggleAttribute('hidden', count === 0);
    });
  };

  const announce = (msg) => {
    const live = drawer()?.querySelector('[data-cart-live]');
    if (live) live.textContent = msg;
  };

  // Keep the first `limit` addable cards; drop overflow and anything now in-cart.
  const filterCards = (track, inCart, limit) => {
    let kept = 0;
    track.querySelectorAll('.cart-drawer__upsell').forEach((card) => {
      if (kept >= limit || inCart.has(card.dataset.upsellPid)) card.remove();
      else kept += 1;
    });
    return kept;
  };

  // A cart mutation's fresh inner arrives with an empty, hidden auto-upsell
  // placeholder. Carry the already-loaded cards into the fresh placeholder
  // synchronously BEFORE it is shown (no blink), dropping any product now in the
  // cart. render() then kicks a NON-destructive background refresh so the shrinking
  // list replenishes with new recommendations without ever blanking the carried cards.
  const carryUpsells = (current, next) => {
    const box = next.querySelector('[data-cart-upsells]');
    const liveTrack = current.querySelector('[data-cart-upsells] [data-cart-upsells-track]');
    if (!box || !liveTrack) return; // manual mode or upsells off
    const track = box.querySelector('[data-cart-upsells-track]');
    track.replaceChildren(...liveTrack.children);
    const inCart = new Set((next.dataset.cartPids || '').split(',').filter(Boolean));
    const kept = filterCards(track, inCart, Number(box.dataset.upsellsLimit) || 4);
    box.toggleAttribute('hidden', kept === 0); // un-hide synchronously when cards remain
  };

  // Swap only the drawer's inner region; keep the <dialog>, script and live region.
  const render = (sections) => {
    const html = sections?.[sectionId()];
    if (!html) return;
    const next = new DOMParser()
      .parseFromString(html, 'text/html')
      .querySelector('[data-cart-drawer-inner]');
    const current = drawer()?.querySelector('[data-cart-drawer-inner]');
    if (!next || !current) return;
    carryUpsells(current, next); // move live cards over before the swap — no blink, no re-fetch
    current.replaceWith(next);
    const count = Number(next.dataset.cartCount);
    if (Number.isFinite(count)) setHeaderCount(count);
    if (drawer()?.open) loadUpsells(); // background refresh; carried cards stay put until fresh ones arrive
  };

  // Read the latest cart product ids straight off the live inner (re-queried, since
  // the inner is replaced on every mutation) — used both to seed the fetch and to
  // re-filter at apply time so a slow response can't resurrect a now-in-cart card.
  const cartPids = () =>
    (drawer()?.querySelector('[data-cart-drawer-inner]')?.dataset.cartPids || '')
      .split(',').filter(Boolean);

  // Auto upsells: fetch product recommendations for the newest cart item. Called on
  // drawer OPEN and as a background refresh after each mutation. NON-destructive: the
  // visible track is never cleared while the fetch is in flight; on arrival we build
  // the filtered list off-DOM and only then swap it in. If nothing addable comes back
  // we keep whatever cards are already showing, hiding the box only when it is empty.
  // Manual mode / upsells off: no-op. Silent on failure (carried cards stay put).
  const loadUpsells = () => {
    const box = drawer()?.querySelector('[data-cart-upsells]');
    const base = box?.dataset.upsellsUrl;
    if (!base) return; // manual mode or upsells off
    const pids = cartPids();
    if (!pids.length) { box.setAttribute('hidden', ''); return; }
    const limit = Number(box.dataset.upsellsLimit) || 4;
    const seed = pids[pids.length - 1]; // newest line item is a sensible seed
    // Over-fetch by the cart size so in-cart filtering still leaves `limit` addable.
    const fetchLimit = Math.min(10, limit + pids.length);
    const url = `${base}?section_id=${sectionId()}&product_id=${seed}&limit=${fetchLimit}&intent=related`;
    const gen = ++upsellGen; // stamp this request; a newer one invalidates it
    fetch(url)
      .then((res) => (res.ok ? res.text() : Promise.reject()))
      .then((html) => {
        if (gen !== upsellGen) return; // stale — a newer refresh/mutation superseded us
        const live = drawer()?.querySelector('[data-cart-upsells-track]');
        const liveBox = drawer()?.querySelector('[data-cart-upsells]');
        if (!live || !liveBox) return;
        const fresh = new DOMParser()
          .parseFromString(html, 'text/html')
          .querySelector('[data-cart-upsells-track]');
        // Filter against the LATEST cart pids, not the ones at fetch-start.
        const kept = fresh ? filterCards(fresh, new Set(cartPids()), limit) : 0;
        if (kept > 0) {
          live.replaceChildren(...fresh.children);
          liveBox.removeAttribute('hidden');
        } else if (!live.children.length) {
          liveBox.setAttribute('hidden', ''); // nothing fresh AND nothing carried
        } // else: keep the carried cards rather than blanking
      })
      .catch(() => {}); // leave the current (carried) cards untouched on failure
  };

  const request = async (url, body) => {
    body.append('sections', sectionId());
    const res = await fetch(url, { method: 'POST', headers: { Accept: 'application/json' }, body });
    const data = await res.json().catch(() => ({}));
    return { ok: res.ok, data };
  };

  const change = (line, quantity) => run(async () => {
    const body = new FormData();
    body.append('line', String(line));
    body.append('quantity', String(quantity));
    const { data } = await request('/cart/change.js', body);
    render(data.sections);
    announce(drawer()?.dataset.updated || 'Cart updated');
  });

  // Cart PAGE live update (opt-in: the section carries data-cart-autoupdate). Stepper and input
  // changes post every quantity to /cart/update.js, then the page section and the drawer are
  // swapped from the Section Rendering response. The Update button only exists for no-JS, so
  // it is hidden here, never in markup. Any failure posts the native form instead.
  const livePage = () => document.querySelector('.cart-page[data-cart-autoupdate]');
  const LIVE_FOCUS = 'input[name="updates[]"], [data-qty-adjust]';
  const hideUpdate = () => livePage()?.querySelectorAll('[data-cart-update]').forEach((b) => { b.hidden = true; });
  hideUpdate();
  let liveTimer;
  const liveUpdate = () => {
    clearTimeout(liveTimer);
    liveTimer = setTimeout(() => run(async () => {
      const page = livePage();
      const form = page?.querySelector('.cart-page__form');
      if (!form) return;
      const pageId = page.closest('.shopify-section')?.id.replace(/^shopify-section-/, '');
      try {
        const res = await fetch('/cart/update.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({
            updates: [...form.querySelectorAll('input[name="updates[]"]')].map((i) => Math.max(0, parseInt(i.value, 10) || 0)),
            sections: [pageId, sectionId()].join(','),
            sections_url: location.pathname,
          }),
        });
        const data = await res.json();
        const next = res.ok && new DOMParser().parseFromString(data.sections?.[pageId] || '', 'text/html').querySelector('.cart-page');
        if (!next) throw new Error('no section');
        const k = [...page.querySelectorAll(LIVE_FOCUS)].indexOf(document.activeElement);
        page.replaceWith(next);
        hideUpdate();
        if (k > -1) next.querySelectorAll(LIVE_FOCUS)[k]?.focus();
        render(data.sections); // the drawer and the header count
      } catch (err) {
        form.submit();
      }
    }), 350);
  };

  document.addEventListener('submit', (e) => {
    const form = e.target.closest('form[action$="/cart/add"]');
    if (!form) return; // any other form (checkout, newsletter) posts normally
    e.preventDefault();
    const body = new FormData(form); // snapshot NOW — render() may replace the form
    const submit = form.querySelector('[type="submit"]');
    const card = form.closest('.cart-drawer__upsell'); // set only for an upsell add
    // Same-frame feedback: disable the button and optimistically fold the tapped
    // upsell card before any network. On success render() replaces it; reverted on failure.
    if (submit) submit.setAttribute('aria-disabled', 'true');
    if (card) card.hidden = true;
    run(async () => {
      const { ok, data } = await request('/cart/add.js', body);
      if (ok) {
        render(data.sections);
        const msg = drawer()?.dataset.added || 'Item added to your cart';
        if (drawer()?.dataset.afterAdd === 'stay' && toast(msg)) {
          bumpCount();
        } else {
          open();
          announce(msg);
        }
      } else {
        if (card) card.hidden = false;
        announce(data.description || data.message || drawer()?.dataset.error || 'Could not add item');
      }
      if (submit) submit.removeAttribute('aria-disabled');
    });
  });

  document.addEventListener('click', (e) => {
    const d = drawer();
    // Cart PAGE −/+ buttons just bump the sibling input; the form posts updates[].
    const adjust = e.target.closest('[data-qty-adjust]');
    if (adjust) {
      const input = adjust.parentElement.querySelector('input[type="number"]');
      if (input) input.value = Math.max(0, (parseInt(input.value, 10) || 0) + Number(adjust.dataset.qtyAdjust));
      if (adjust.closest('[data-cart-autoupdate]')) liveUpdate();
      return;
    }
    if (e.target.closest('[data-cart-toggle]')) {
      e.preventDefault();
      open();
      return;
    }
    if (e.target.closest('[data-cart-close]')) {
      e.preventDefault();
      close();
      return;
    }
    if (d && d.open && e.target === d) {
      close(); // click on the backdrop (the dialog element itself)
      return;
    }
    const step = e.target.closest('[data-qty-up], [data-qty-down]');
    if (step) {
      const line = step.dataset.line;
      const input = d.querySelector(`[data-qty-input][data-line="${line}"]`);
      const q = (parseInt(input?.value, 10) || 0) + (step.hasAttribute('data-qty-up') ? 1 : -1);
      change(line, Math.max(0, q));
      return;
    }
    const remove = e.target.closest('[data-cart-remove]');
    if (remove) change(remove.dataset.line, 0);
  });

  document.addEventListener('change', (e) => {
    const input = e.target.closest('[data-qty-input]');
    if (input) change(input.dataset.line, Math.max(0, parseInt(input.value, 10) || 0));
    else if (e.target.closest('[data-cart-autoupdate] input[name="updates[]"]')) liveUpdate();
  });
})();
